function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ChipButton, ConfirmationModal, DropdownMenu, FilterPickerGroup, FunctionalLoader, Page, PageContentHeader, PageEmptyState, PageErrorState, Searchbox, TableList, Typography, useStateInRef } from 'seagull';
import { ArchiveFill, CopyFill, Plus, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import countBy from 'lodash/countBy';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { routes } from "../../constants";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { useServiceTemplateApi } from "../../hooks/useServiceTemplateApi";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { selectors } from "../../store/state-selectors";
import { serviceModel, serviceTemplateModel } from "../../models";
import EmptyServices from "./emptyServices.svg";
import ServiceBuilder from "../../components/ServiceCreation/ServiceBuilder/ServiceBuilder";
import { baseColumnDefs, baseHeaderItems, defaultSort, getDuplicatedServices, getServiceNameValidation, saveServiceTemplate, sortFunctions, sortIds } from "./ServiceLibrary.utils";
import { ServiceRow } from "./ServiceRow";
import ServiceLibrarySeeWhatItsLike from "./SeeWhatItsLike/SeeWhatItsLike";
import styles from "./ServiceLibrary.module.scss";
import { handlePromise, numberAndUnit } from "../../infra/utils";
import { ServiceSource } from "../../models/service/model";
const title = 'Services';
export const importServicesRoute = {
  id: 'import-services',
  text: 'Import services',
  url: routes.importServices,
  shouldShow: _ref => {
    let {
      isVendor
    } = _ref;
    return isVendor;
  }
};
export function ServiceLibrary() {
  const overlayContext = useOverlayContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const importServicesLink = useStore(selectors.filterAvailableRoutes([importServicesRoute]))[0];
  const {
    data,
    loading,
    error
  } = useStore(state => state.serviceTemplates);
  const qboItems = useStore(state => state.qboItems);
  const {
    fetchServiceTemplates,
    fetchQboItems,
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    urlState: {
      search
    },
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    }
  });
  const [selected, setSelected] = useState({});
  const getSelected = useStateInRef(selected);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const numSelected = useMemo(() => {
    var _countBy$true;
    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  const allServices = data === null || data === void 0 ? void 0 : data.active;
  useEffect(() => {
    document.title = title;
    fetchServiceTemplates();
  }, []);
  useEffect(() => {
    // this unselects any service that is not found in the newly fetched list of services
    if (!data || isEmpty(getSelected())) return;
    const ids = data.active.map(service => service.id);
    const newSelected = mapValues(getSelected(), (origValue, serviceId) => ids.includes(serviceId) ? origValue : false);
    setSelected(newSelected);
  }, [data]);
  useEffect(() => {
    if (showQboIntegration) {
      Promise.all([fetchQboItems(), fetchIntegrations()]);
    }
  }, [showQboIntegration]);
  const columnDefs = baseColumnDefs;
  const headerItems = baseHeaderItems;
  const qboItemsById = useMemo(() => {
    var _qboItems$data;
    return keyBy((_qboItems$data = qboItems.data) === null || _qboItems$data === void 0 ? void 0 : _qboItems$data.items, 'id');
  }, [qboItems.data]);
  const services = useMemo(() => {
    if (!data) return null;
    const normalizedSearch = search ? search.trim().toLowerCase() : search;
    const filtered = data.active.filter(service => {
      return !normalizedSearch || service.name.toLowerCase().includes(normalizedSearch);
    });
    if (sort) {
      filtered.sort(sortFunctions[sort.colIndex](sort.sort === 'asc', qboItemsById));
    }
    return filtered;
  }, [data, qboItemsById, search, sort]);
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  function onToggleAllSelection(selectAll) {
    if (services == null) return;
    setSelected(services.reduce((acc, _ref2) => {
      let {
        id
      } = _ref2;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }
  async function editService(originalService, edit) {
    try {
      logBI(11101, {
        serviceId: originalService.id
      });
      await serviceTemplateApi.update(_objectSpread(_objectSpread({}, originalService), edit));
      await fetchServiceTemplates();
    } catch (err) {
      overlayContext.showErrorToast('The changes were not saved. Please try again', 'service-library-update-error-toast');
    }
  }
  function getSelectedServices() {
    const services = keyBy(data === null || data === void 0 ? void 0 : data.active, 'id');
    return Object.entries(selected).filter(_ref3 => {
      let [, isSelected] = _ref3;
      return isSelected;
    }).flatMap(_ref4 => {
      let [serviceId] = _ref4;
      return serviceId in services ? services[serviceId] : [];
    });
  }
  function bulkActionDecorator(action) {
    return async () => {
      const promises = getSelectedServices().map(service => action(service, false));
      await Promise.all(promises);
      await fetchServiceTemplates();
    };
  }
  async function duplicateServices(services) {
    try {
      overlayContext.startLoader(title);
      services.forEach(s => logBI(11104, {
        serviceId: s.id
      }));
      const duplicatedServices = getDuplicatedServices(services, allServices);
      await Promise.all(duplicatedServices.map(serviceTemplateApi.create));
      await fetchServiceTemplates();
    } catch (err) {
      overlayContext.showErrorToast('Failed to duplicate service');
    } finally {
      overlayContext.stopLoader(title);
    }
  }
  async function doDelete(service) {
    let refetch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    try {
      logBI(11103, {
        serviceId: service.id
      });
      overlayContext.startLoader(title);
      await serviceTemplateApi.delete(service.id);
      if (refetch) {
        await fetchServiceTemplates();
      }
    } catch (err) {
      overlayContext.showErrorToast('Failed to delete service');
    } finally {
      overlayContext.stopLoader(title);
    }
  }
  async function deleteService(service) {
    const confirmed = await overlayContext.showModal({
      closeOnClickOutside: true,
      shouldBlurBackground: true,
      closeOnEscapeKey: true,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          title: "Delete this service?",
          content: "Once this service is deleted you won't be able to access it",
          skin: "danger",
          primaryButtonLabel: "Delete",
          onPrimaryButtonClick: () => dismissModal(true),
          secondaryButtonLabel: "Cancel",
          onSecondaryButtonClick: () => dismissModal(false)
        });
      }
    });
    if (confirmed) {
      return doDelete(service);
    }
  }
  async function openServiceBuilder(serviceToEdit) {
    if (serviceToEdit) {
      logBI(11102, {
        serviceId: serviceToEdit.id
      });
    }
    const serviceNameBlacklist = data === null || data === void 0 ? void 0 : data.active.filter(s => s.id !== (serviceToEdit === null || serviceToEdit === void 0 ? void 0 : serviceToEdit.id)).map(s => s.name);
    const serviceTemplateSaverListener = {
      saveStarted: () => overlayContext.startLoader(title),
      saveFailed: () => overlayContext.showErrorToast('Failed to save service', 'save-service-fail-toast'),
      saveFinished: () => overlayContext.stopLoader(title),
      linkFailed: () => overlayContext.showErrorToast('Failed to link service', 'link-service-fail-toast')
    };
    const serviceTemplateRepo = {
      create: serviceTemplateApi.create,
      update: serviceTemplateApi.update
    };
    const storePopulator = {
      populateServiceTemplates: fetchServiceTemplates
    };
    const onSave = output => {
      return handlePromise(saveServiceTemplate({
        listener: serviceTemplateSaverListener,
        repo: serviceTemplateRepo,
        modifiedService: serviceTemplateModel.fromService(output),
        originalService: serviceToEdit,
        storePopulator
      }));
    };
    setRegisterSearchBoxHotkey(false);
    try {
      const service = serviceToEdit && serviceModel.fromServiceTemplate(serviceToEdit, ServiceSource.Draft);
      await overlayContext.showModal({
        content: p => /*#__PURE__*/React.createElement(ServiceBuilder, _extends({}, p, {
          serviceOrigin: "template",
          service: service,
          serviceNameBlacklist: serviceNameBlacklist,
          onSave: onSave
        }))
      });
    } finally {
      setRegisterSearchBoxHotkey(true);
    }
  }
  return error ? /*#__PURE__*/React.createElement(PageErrorState, {
    title: title,
    onRefresh: () => fetchServiceTemplates()
  }) : !data ? /*#__PURE__*/React.createElement(FunctionalLoader, {
    show: true,
    name: title
  }) : /*#__PURE__*/React.createElement(Page, {
    skin: "secondary",
    "data-testid": "service-library",
    header: title + (data && data.active.length > 0 ? " (".concat(data.active.length, ")") : ''),
    subheader: "Create and manage the services you provide to clients. You may customize the services per client when generating each new proposal.",
    loading: loading,
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, importServicesLink && /*#__PURE__*/React.createElement(Button, {
      skin: "primaryOutline",
      as: Link,
      to: importServicesLink.url
    }, importServicesLink.text), /*#__PURE__*/React.createElement(Button, {
      prefixIcon: /*#__PURE__*/React.createElement(Plus, {
        height: 18,
        width: 18
      }),
      "data-testid": "service-library-new-btn",
      onClick: () => openServiceBuilder()
    }, "New service"))
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: sort,
    onSortChange: changeSort,
    enableRowSelection: true,
    selectedRows: selected,
    onSelectionChange: setSelected,
    numRows: (services === null || services === void 0 ? void 0 : services.length) || 0,
    numSelectedRows: numSelected,
    onToggleAllSelection: onToggleAllSelection
  }, (data === null || data === void 0 ? void 0 : data.active.length) > 0 && /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement(FilterPickerGroup, null, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: registerSearchBoxHotkey,
    value: search,
    onChange: event => {
      const search = event.target.value;
      changeUrlState({
        search
      });
    },
    onClear: () => changeUrlState({
      search: ''
    }),
    "data-testid": "service-library-search"
  }), /*#__PURE__*/React.createElement(Typography, {
    className: styles.selectedItems,
    "data-testid": "service-library-bulk-counter"
  }, numSelected > 0 ? "".concat(numberAndUnit(numSelected, 'service'), " selected") : ''), /*#__PURE__*/React.createElement(DropdownMenu, {
    onOpenChange: setBulkActionsOpen,
    target: /*#__PURE__*/React.createElement(ChipButton, {
      "data-testid": "service-library-bulk-actions",
      suffixIcon: bulkActionsOpen ? /*#__PURE__*/React.createElement(WideArrowUpward, null) : /*#__PURE__*/React.createElement(WideArrowDownward, null),
      highlight: bulkActionsOpen,
      disabled: numSelected === 0
    }, "Bulk actions")
  }, /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    iconStart: /*#__PURE__*/React.createElement(CopyFill, null),
    "data-testid": "service-library-bulk-duplicate",
    onClick: () => duplicateServices(getSelectedServices())
  }, "Duplicate"), /*#__PURE__*/React.createElement(DropdownMenu.Item, {
    skin: "error",
    iconStart: /*#__PURE__*/React.createElement(ArchiveFill, null),
    "data-testid": "service-library-bulk-delete",
    onClick: bulkActionDecorator(doDelete)
  }, "Delete"))), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "service-library-table-header"
  })), services === null || services === void 0 ? void 0 : services.map(service => /*#__PURE__*/React.createElement(ServiceRow, {
    key: service.id,
    service: service,
    qboItemsById: qboItemsById,
    serviceNameValidation: getServiceNameValidation({
      skipServiceId: service.id,
      allServices
    }),
    onInlineEdit: edit => editService(service, edit),
    onEdit: () => openServiceBuilder(service),
    onDuplicate: () => duplicateServices([service]),
    onDelete: () => deleteService(service),
    onInputFocus: () => setRegisterSearchBoxHotkey(false),
    onInputBlur: () => setRegisterSearchBoxHotkey(true)
  })), (data === null || data === void 0 ? void 0 : data.active.length) === 0 && /*#__PURE__*/React.createElement(PageEmptyState, {
    "data-testid": "services-empty",
    illustration: /*#__PURE__*/React.createElement(EmptyServices, null),
    title: "Organize Your Business with Services",
    message: "Make the process of managing new proposals easier by adding services. You can always create new services, edit or delete existing ones in the future.",
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ServiceLibrarySeeWhatItsLike, null), /*#__PURE__*/React.createElement(Button, {
      "data-testid": "service-library-new-btn",
      onClick: () => openServiceBuilder()
    }, "Create a service"))
  })));
}